/**
 * Pure helpers for the Multi (parallel multi-model generation) section.
 *
 * A workflow opts into Multi via the `multi_task_type` column (see Task 1).
 * These helpers are DOM-free and fully unit-tested; the page component wires
 * them to fetch + localStorage.
 */

/**
 * Canonical ordering, shared by tabs and the admin dropdown.
 *
 * Deliberately a literal rather than `MultiTaskTypeSchema.options`: importing
 * `@functions/schemas/database` from `src/` pulls zod plus that whole (2900-line)
 * schema module into the main client entry chunk — ~+100 kB raw on every page —
 * for what is an 8-value string enum. `MultiTaskTypeSchema` remains the source of
 * truth; `tests/unit/multi-task-type-schema.test.ts` fails if the two drift.
 */
export const MULTI_TASK_TYPES = [
  'text2image',
  'image2image',
  'text2video',
  'image2video',
  'video2video',
  'text2_3d',
  'image2_3d',
  'multiimage2_3d',
] as const;

export type MultiTaskType = (typeof MULTI_TASK_TYPES)[number];

export interface TaskTypeMeta {
  id: MultiTaskType;
  /** Shared input requires a source image slot. */
  needsImage: boolean;
  /** Shared input requires a source video slot. */
  needsVideo: boolean;
  /** 3D output type (tabs still hidden until such workflows exist). */
  is3d: boolean;
  /** Shared input renders four named view slots instead of one source slot. */
  multiView?: boolean;
}

export const TASK_TYPE_META: Record<MultiTaskType, TaskTypeMeta> = {
  text2image: { id: 'text2image', needsImage: false, needsVideo: false, is3d: false },
  image2image: { id: 'image2image', needsImage: true, needsVideo: false, is3d: false },
  text2video: { id: 'text2video', needsImage: false, needsVideo: false, is3d: false },
  image2video: { id: 'image2video', needsImage: true, needsVideo: false, is3d: false },
  video2video: { id: 'video2video', needsImage: false, needsVideo: true, is3d: false },
  text2_3d: { id: 'text2_3d', needsImage: false, needsVideo: false, is3d: true },
  image2_3d: { id: 'image2_3d', needsImage: true, needsVideo: false, is3d: true },
  multiimage2_3d: {
    id: 'multiimage2_3d',
    needsImage: true,
    needsVideo: false,
    is3d: true,
    multiView: true,
  },
};

/** Minimal shape of an item from `/api/user/workflows/available`. */
export interface MultiWorkflowItem {
  workflow?: { multi_task_type?: string | null; [key: string]: unknown } | null;
  [key: string]: unknown;
}

export function isMultiTaskType(value: unknown): value is MultiTaskType {
  return typeof value === 'string' && (MULTI_TASK_TYPES as readonly string[]).includes(value);
}

/** Leading punctuation left behind after a stripped prefix ("Text2Image — Foo"). */
const LEADING_SEPARATORS = /^[\s\-_:;.,–—>/|]+/;

const alphanumeric = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Accepted spellings of a task type inside a workflow title, normalized to
 * alphanumerics: `image2_3d` → `image23d` ("Image2 3D") and `imageto3d`
 * ("ImageTo3D"). Separators/case in the title are irrelevant.
 */
function taskTypeAliases(taskType: MultiTaskType): string[] {
  const parts = taskType.split('2').map(alphanumeric);
  return [parts.join('2'), parts.join('to')];
}

/**
 * Drops a redundant task-type prefix from a model title: on the `text2image`
 * list every entry reads "Text2Image Nano Banana", and the prefix is the one
 * thing the user already picked — it only eats the width the actual model name
 * needs.
 *
 * Returns the title unchanged when it carries no such prefix, and — importantly
 * — when stripping would leave nothing (a workflow literally named
 * "Text2Image"), because a blank row is worse than a redundant one.
 */
export function stripTaskTypePrefix(
  title: string,
  taskType: MultiTaskType | null | undefined
): string {
  if (!title || !taskType) return title;

  const aliases = taskTypeAliases(taskType);
  const longest = Math.max(...aliases.map((alias) => alias.length));
  let seen = '';

  for (let i = 0; i < title.length; i++) {
    const char = title[i].toLowerCase();
    if (char >= 'a' && char <= 'z') seen += char;
    else if (char >= '0' && char <= '9') seen += char;
    // Anything else (space, dash, colon) is a separator: it never breaks a
    // prefix match, so "Text 2 Image Foo" strips the same as "Text2ImageFoo".

    if (aliases.includes(seen)) {
      const rest = title.slice(i + 1).replace(LEADING_SEPARATORS, '').trim();
      return rest || title;
    }
    // Past the longest alias the prefix can no longer match — stop scanning the
    // whole (possibly long) title.
    if (seen.length >= longest) break;
  }

  return title;
}

/** Reads the (validated) multi_task_type off an available-workflow item. */
export function getWorkflowTaskType(item: MultiWorkflowItem | null | undefined): MultiTaskType | null {
  const value = item?.workflow?.multi_task_type;
  return isMultiTaskType(value) ? value : null;
}

/**
 * Archived workflows are hidden from Multi, matching the classic picker
 * (`WorkflowTileSelector` filters them out unless "Show archived" is on).
 * Multi has no such toggle, so archiving must actually remove the model.
 */
export function isArchivedWorkflow(item: MultiWorkflowItem | null | undefined): boolean {
  return (item?.workflow?.display_status as string | undefined) === 'archived';
}

/** Only non-archived workflows tagged with the given task type. */
export function filterWorkflowsByTaskType<T extends MultiWorkflowItem>(
  items: T[] | null | undefined,
  taskType: MultiTaskType
): T[] {
  return (items || []).filter(
    (item) => getWorkflowTaskType(item) === taskType && !isArchivedWorkflow(item)
  );
}

/** Task types that have at least one available (non-archived) workflow, in canonical order. */
export function visibleTaskTabs(items: MultiWorkflowItem[] | null | undefined): MultiTaskType[] {
  const present = new Set<MultiTaskType>();
  for (const item of items || []) {
    if (isArchivedWorkflow(item)) continue;
    const type = getWorkflowTaskType(item);
    if (type) present.add(type);
  }
  return MULTI_TASK_TYPES.filter((type) => present.has(type));
}

/**
 * Resolve which tab should be active given the saved preference and the tabs
 * currently visible. Falls back to the first visible tab; null when none.
 */
export function resolveActiveTab(
  visible: MultiTaskType[],
  saved: MultiTaskType | null
): MultiTaskType | null {
  if (saved && visible.includes(saved)) return saved;
  return visible.length > 0 ? visible[0] : null;
}

// ---------------------------------------------------------------------------
// localStorage persistence (per-tab model selection + active tab)
// ---------------------------------------------------------------------------

const SELECTED_MODELS_PREFIX = 'multi-selected-models:';
const ACTIVE_TAB_KEY = 'multi-active-tab';

/** Storage abstraction so helpers stay testable without a real DOM. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): KeyValueStorage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    /* access to localStorage can throw (privacy mode) */
  }
  return null;
}

/**
 * Selection is persisted PER ORGANIZATION. Without the org id in the key, an
 * org switch restores the previous org's checkbox set — and any model id that
 * exists in both orgs comes back pre-checked and counted in "Generate (N)",
 * so the user can launch (and pay for) models they never selected here.
 * `orgId` is a required parameter so a caller cannot silently write the
 * unscoped legacy key.
 */
export function selectedModelsKey(
  taskType: MultiTaskType,
  orgId: number | string | null | undefined
): string {
  return `${SELECTED_MODELS_PREFIX}${orgId ?? 'none'}:${taskType}`;
}

/** Serialize a set of model ids (numbers or visual_ids) to a stable string. */
export function serializeSelectedModels(ids: Array<string | number>): string {
  return JSON.stringify(ids.map(String));
}

/** Parse persisted selection back to string ids, tolerating garbage. */
export function parseSelectedModels(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((value) => typeof value === 'string' || typeof value === 'number')
      .map(String);
  } catch {
    return [];
  }
}

export function saveSelectedModels(
  taskType: MultiTaskType,
  orgId: number | string | null | undefined,
  ids: Array<string | number>,
  storage: KeyValueStorage | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(selectedModelsKey(taskType, orgId), serializeSelectedModels(ids));
  } catch {
    /* ignore write failures */
  }
}

export function loadSelectedModels(
  taskType: MultiTaskType,
  orgId: number | string | null | undefined,
  storage: KeyValueStorage | null = defaultStorage()
): string[] {
  if (!storage) return [];
  try {
    return parseSelectedModels(storage.getItem(selectedModelsKey(taskType, orgId)));
  } catch {
    return [];
  }
}

export function saveActiveTab(
  taskType: MultiTaskType,
  storage: KeyValueStorage | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(ACTIVE_TAB_KEY, taskType);
  } catch {
    /* ignore */
  }
}

export function loadActiveTab(
  storage: KeyValueStorage | null = defaultStorage()
): MultiTaskType | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(ACTIVE_TAB_KEY);
    return isMultiTaskType(value) ? value : null;
  } catch {
    return null;
  }
}
